import { Select, Tabs, Tooltip } from "antd";
import { BsFillGrid3X3GapFill } from "react-icons/bs";
import { FaList } from "react-icons/fa";
import { MdOutlineSort } from "react-icons/md";
import { IoDiamond } from "react-icons/io5";
import { MdVerified } from "react-icons/md";
import "./homStyle.css";
import { VERIFIED_TAB_ENABLED } from "../hooks/useHomeAds";


interface AdsControlsProps {
    activeTab: 'all' | 'premium' | 'verified';
    displayAds: "grid" | "list";
    sort: string;
    onTabChange: (key: 'all' | 'premium' | 'verified') => void;
    onDisplayChange: (type: "grid" | "list") => void;
    onSortChange: (value: string) => void;
}

const AdsControls: React.FC<AdsControlsProps> = ({
    activeTab,
    displayAds,
    sort,
    onTabChange,
    onDisplayChange,
    onSortChange
}) => {
    const tabItems = [
        {
            key: 'all',
            label: (
                <span className="flex items-center gap-1 text-sm">
                    <span>Toutes les annonces</span>
                </span>
            )
        },
        {
            key: 'premium',
            label: (
                <span className="flex items-center gap-1 text-sm">
                    <IoDiamond className="text-amber-500 flex-shrink-0" />
                    <span>Premium</span>
                </span>
            )
        },
        {
            key: 'verified',
            // greyed out while the feature is switched off (VERIFIED_TAB_ENABLED)
            disabled: !VERIFIED_TAB_ENABLED,
            label: (
                <span className="flex items-center gap-1 text-sm">
                    <MdVerified className={`flex-shrink-0 ${VERIFIED_TAB_ENABLED ? 'text-accent-default' : ''}`} />
                    <span>Vérifiés</span>
                </span>
            )
        }
    ];

    return (
        <div className="bg-white rounded-xl shadow-sm mb-4 md:mb-6 p-3 md:p-4">
            <div className="flex flex-col xl:flex-row xl:items-center gap-2 xl:gap-4">
                {/* Tabs */}
                <div className="flex-1 min-w-0">
                    <Tabs
                        activeKey={activeTab}
                        onChange={(key) => onTabChange(key as 'all' | 'premium' | 'verified')}
                        className="w-full"
                        // no overflow style here: antd handles tabs that don't fit itself, and
                        // overflowX 'auto' also made a tiny vertical scrollbar (▲●▼) appear
                        tabBarStyle={{ marginBottom: 0 }}
                        items={tabItems.map(item => ({
                            key: item.key,
                            disabled: item.disabled,
                            label: (
                                <div className="flex items-center gap-1 py-1">
                                    {item.label}
                                </div>
                            )
                        }))}
                    />
                </div>

                {/* Display & Sort controls */}
                <div className="flex items-center gap-4 flex-shrink-0">
                    <div className="flex items-center gap-2">
                        <Tooltip title="Vue liste">
                            <button
                                onClick={() => onDisplayChange("list")}
                                className={`p-2 rounded-lg transition-colors ${displayAds === 'list' ? 'bg-primary-tertiary text-primary-default' : 'text-gray-400 hover:text-gray-600'}`}
                                aria-label="Vue liste"
                            >
                                <FaList size={16} />
                            </button>
                        </Tooltip>
                        <Tooltip title="Vue grille">
                            <button
                                onClick={() => onDisplayChange("grid")}
                                className={`p-2 rounded-lg transition-colors ${displayAds === 'grid' ? 'bg-primary-tertiary text-primary-default' : 'text-gray-400 hover:text-gray-600'}`}
                                aria-label="Vue grille"
                            >
                                <BsFillGrid3X3GapFill size={16} />
                            </button>
                        </Tooltip>
                    </div>

                    <div className="flex items-center gap-2 border-l pl-4">
                        <MdOutlineSort className="text-gray-500" size={18} />
                        <Select
                            placeholder="Trier par"
                            onChange={onSortChange}
                            value={sort}
                            className="w-36"
                            options={[
                                { value: "", label: 'Plus récent' },
                                { value: "high", label: 'Prix élevé' },
                                { value: "low", label: 'Prix bas' },
                            ]}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdsControls;